import { useQuery } from '@apollo/client';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import introBackground from '@volumental/assets/images/scanner/IntroPage.png';
import { Container, isMobileDevice, Loader } from '@volumental/common';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from '../../api/gql';
import {
  usePersistedViewStore,
  useScannerStore,
  useSessionStore,
  useViewStore,
} from '../../store';
import {
  AppMode,
  CookieType,
  CurrentView,
  UserFlowType,
} from '../../types/types';
import { GeneralErrorPage } from '../error/GeneralErrorPage';
import { CookieModal } from '../scanner/cookie-modal/cookie-modal';
import { ScannerVariant } from '../scanner/utils';
import { StepListIntro } from './step-list-intro';
import { TopBannerImageIntro } from './top-banner-image-intro';

const GetIntroViewConfigurationQuery = graphql(`
  query getIntroViewConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
      features {
        recommendation {
          enableSurvey
        }
        enableProfiles
      }
    }
  }
`);

interface IntroViewProps {
  onClose: () => void;
}
export function IntroView({ onClose }: IntroViewProps): JSX.Element {
  const { loading, error, data } = useQuery(GetIntroViewConfigurationQuery);
  const { t } = useTranslation();
  const { mode } = useSessionStore((store) => store);
  const { setFlowType, setScannerVariant } = useScannerStore((store) => store);
  usePersistedViewStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const { cookieType } = usePersistedViewStore((store) => store);
  const [showCookieModal, setShowCookieModal] = useState(false);
  if (loading) {
    return <Loader />;
  }
  if (error || !data) {
    return (
      <GeneralErrorPage
        errorText="Missing IntroView configuration"
        onClose={onClose}
      />
    );
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
    features: {
      recommendation: { enableSurvey },
      enableProfiles,
    },
  } = data.configuration;

  const onNext = (): void => {
    usageTracker.track(UsageTrackerEvent.ContinueFromIntro);
    if (enableSurvey && mode === AppMode.Recommendation) {
      setCurrentView(CurrentView.SizePicker);
    } else if (!isMobileDevice) {
      setCurrentView(CurrentView.ScannerDesktop);
    } else if (!enableSurvey && allowSelfScanning && !allowAssistedScanning) {
      // this only check if we have solo on
      setFlowType(UserFlowType.Solo);
      setScannerVariant(ScannerVariant.Solo);
      usageTracker.track(UsageTrackerEvent.IntroLoadSolo, {
        sizing_flow_type: UserFlowType.Solo,
      });
      setCurrentView(CurrentView.SoloOnboarding);
    } else {
      setCurrentView(CurrentView.Picker);
    }
  };

  const onClickAction = (): void => {
    if (cookieType === CookieType.Decline) {
      setShowCookieModal(true);
    } else {
      onNext();
    }
  };
  const onSignInAction = (): void => {
    setCurrentView(CurrentView.HistorySignIn);
  };

  return (
    <Container>
      {enableSurvey && mode === AppMode.Recommendation ? (
        <StepListIntro
          mainBtTitle={t('button.getStarted')}
          onClick={onClickAction}
          showAlternative={enableProfiles}
          onAlternative={onSignInAction}
          alternativeBtTitle={t('accessMySavedResults')}
          onClose={onClose}
        />
      ) : (
        <TopBannerImageIntro
          bannerImg={introBackground}
          headLine={
            mode === AppMode.Measurement
              ? t('scanYourFeet')
              : t('findYourBestSizeIntro')
          }
          mainBtTitle={t('button.getStarted')}
          onClick={onClickAction}
          showAlternative={enableProfiles}
          onAlternative={onSignInAction}
          alternativeBtTitle={t('accessMySavedResults')}
          onClose={onClose}
        />
      )}
      <CookieModal
        show={showCookieModal}
        close={() => {
          setShowCookieModal(false);
        }}
        next={onNext}
      />
    </Container>
  );
}
